import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type NotificationCategory =
  | 'forum'
  | 'support'
  | 'deliverable'
  | 'evaluation'
  | 'certificate'
  | 'badge'
  | 'session'
  | 'deadline'
  | 'referral'
  | 'system';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  category: NotificationCategory;
  link?: string | null;
  read: boolean;
  createdAt: Date;
}

export interface NotificationSettings {
  inApp: boolean;
  reminders: boolean;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  settings: NotificationSettings;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  updateSettings: (settings: NotificationSettings) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

const PAGE_SIZE = 20;

const mapRow = (row: any): Notification => ({
  id: row.id,
  title: row.title,
  message: row.message,
  type: (row.type ?? 'info') as Notification['type'],
  category: (row.category ?? 'system') as NotificationCategory,
  link: row.link,
  read: !!row.read,
  createdAt: new Date(row.created_at),
});

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings>({ inApp: true, reminders: true });

  const fetchPage = useCallback(
    async (offset: number) => {
      if (!user) return;
      setIsLoading(true);
      const { data, error } = await supabase
        .from('notifications')
        .select('id, title, message, type, category, link, read, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);
      setIsLoading(false);
      if (error) {
        console.error('Error loading notifications', error);
        return;
      }
      const rows = (data ?? []).map(mapRow);
      setHasMore(rows.length === PAGE_SIZE);
      setNotifications((prev) => {
        if (offset === 0) return rows;
        const ids = new Set(prev.map((n) => n.id));
        return [...prev, ...rows.filter((n) => !ids.has(n.id))];
      });
    },
    [user],
  );

  // Carga inicial + preferencias
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }
    fetchPage(0);

    supabase
      .from('notification_preferences')
      .select('in_app_enabled, reminders_enabled')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setSettings({
            inApp: data.in_app_enabled ?? true,
            reminders: data.reminders_enabled ?? true,
          });
        }
      });
  }, [user, fetchPage]);

  // Realtime
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const n = mapRow(payload.new);
            setNotifications((prev) => (prev.some((p) => p.id === n.id) ? prev : [n, ...prev]));
          } else if (payload.eventType === 'UPDATE') {
            const n = mapRow(payload.new);
            setNotifications((prev) => prev.map((p) => (p.id === n.id ? n : p)));
          } else if (payload.eventType === 'DELETE') {
            setNotifications((prev) => prev.filter((p) => p.id !== (payload.old as any).id));
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const loadMore = useCallback(() => {
    fetchPage(notifications.length);
  }, [fetchPage, notifications.length]);

  const markAsRead = useCallback(
    async (id: string) => {
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      await supabase.from('notifications').update({ read: true }).eq('id', id);
    },
    [],
  );

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    await supabase.from('notifications').update({ read: true }).eq('user_id', user.id).eq('read', false);
  }, [user]);

  const deleteNotification = useCallback(async (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    await supabase.from('notifications').delete().eq('id', id);
  }, []);

  const updateSettings = useCallback(
    async (newSettings: NotificationSettings) => {
      setSettings(newSettings);
      if (!user) return;
      await supabase.from('notification_preferences').upsert(
        {
          user_id: user.id,
          in_app_enabled: newSettings.inApp,
          reminders_enabled: newSettings.reminders,
        },
        { onConflict: 'user_id' },
      );
    },
    [user],
  );

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        settings,
        isLoading,
        hasMore,
        loadMore,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        updateSettings,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
